/** The six stored task statuses. OVERDUE is deliberately absent — it is derived. */
export const TASK_STATUS = {
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  SUBMITTED: 'SUBMITTED',
  RETURNED: 'RETURNED',
  APPROVED: 'APPROVED',
  BLOCKED: 'BLOCKED',
} as const
export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS]
export const TASK_STATUSES: readonly TaskStatus[] = Object.values(TASK_STATUS)

/**
 * Statuses where the clock is still running. A task is overdue only if it is in one of
 * these AND its effective deadline has passed. BLOCKED is excluded on purpose: the
 * employee cannot proceed, so counting it against them would be wrong.
 */
export const ACTIVE_TASK_STATUSES: readonly TaskStatus[] = [
  TASK_STATUS.ASSIGNED,
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.RETURNED,
]

export const ROLE = {
  SUPER_MANAGER: 'SUPER_MANAGER',
  MANAGER: 'MANAGER',
  EMPLOYEE: 'EMPLOYEE',
} as const
export type Role = (typeof ROLE)[keyof typeof ROLE]

export const TASK_PRIORITY = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const
export type TaskPriority = (typeof TASK_PRIORITY)[keyof typeof TASK_PRIORITY]

export const PLAN = {
  STARTER: 'STARTER',
  GROWTH: 'GROWTH',
  ENTERPRISE: 'ENTERPRISE',
} as const
export type Plan = (typeof PLAN)[keyof typeof PLAN]

/** null means unlimited. A sentinel like Infinity or -1 invites arithmetic bugs. */
export const PLAN_SEAT_LIMITS: Record<Plan, number | null> = {
  [PLAN.STARTER]: 20,
  [PLAN.GROWTH]: 100,
  [PLAN.ENTERPRISE]: null,
}

export const SUBMISSION_CHANNEL = {
  PLATFORM: 'PLATFORM',
  EXTERNAL: 'EXTERNAL',
} as const
export type SubmissionChannel = (typeof SUBMISSION_CHANNEL)[keyof typeof SUBMISSION_CHANNEL]

export const REVIEW_OUTCOME = {
  APPROVED: 'APPROVED',
  RETURNED: 'RETURNED',
} as const
export type ReviewOutcome = (typeof REVIEW_OUTCOME)[keyof typeof REVIEW_OUTCOME]

export const TASK_EVENT_TYPE = {
  CREATED: 'CREATED',
  STATUS_CHANGED: 'STATUS_CHANGED',
  NOTE_ADDED: 'NOTE_ADDED',
  DEADLINE_EXTENDED: 'DEADLINE_EXTENDED',
  EXTERNAL_DELAY_FLAGGED: 'EXTERNAL_DELAY_FLAGGED',
  BLOCKED_FLAGGED: 'BLOCKED_FLAGGED',
  UNBLOCKED: 'UNBLOCKED',
  COMPLEXITY_CHANGED: 'COMPLEXITY_CHANGED',
  REASSIGNED: 'REASSIGNED',
  ESCALATED: 'ESCALATED',
  LEAVE_EXTENSION: 'LEAVE_EXTENSION',
} as const
export type TaskEventType = (typeof TASK_EVENT_TYPE)[keyof typeof TASK_EVENT_TYPE]

/**
 * Audience-facing notification vocabulary. Deliberately coarser than TaskEventType:
 * the bell's words are a product surface, the audit trail's are forensic.
 */
export const NOTIFICATION_TYPE = {
  ASSIGNED: 'ASSIGNED',
  REASSIGNED: 'REASSIGNED',
  SUBMITTED: 'SUBMITTED',
  RETURNED: 'RETURNED',
  APPROVED: 'APPROVED',
  BLOCKED: 'BLOCKED',
  UNBLOCKED: 'UNBLOCKED',
  DEADLINE_MOVED: 'DEADLINE_MOVED',
  DEPENDENCY_CLEARED: 'DEPENDENCY_CLEARED',
  ESCALATED_STAGE_1: 'ESCALATED_STAGE_1',
  ESCALATED_STAGE_2: 'ESCALATED_STAGE_2',
  INNOVATION_REVIEWED: 'INNOVATION_REVIEWED',
  LEAVE_REQUESTED: 'LEAVE_REQUESTED',
  LEAVE_REVIEWED: 'LEAVE_REVIEWED',
} as const
export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE]

/** Spec §2.6. Named constants, not inline literals, per the scoring precedent. */
export const ESCALATION_STAGE_1_HOURS = 24
export const ESCALATION_STAGE_2_HOURS = 72

/** Spec M3b §2.1: the three PRD cadences. Custom intervals are out of scope. */
export const RECURRENCE_CADENCE = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
} as const
export type RecurrenceCadence = (typeof RECURRENCE_CADENCE)[keyof typeof RECURRENCE_CADENCE]

export const LEAVE_TYPE = {
  ANNUAL: 'ANNUAL',
  SICK: 'SICK',
  UNPAID: 'UNPAID',
  OTHER: 'OTHER',
} as const
export type LeaveType = (typeof LEAVE_TYPE)[keyof typeof LEAVE_TYPE]

export const LEAVE_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
  WITHDRAWN: 'WITHDRAWN',
} as const
export type LeaveStatus = (typeof LEAVE_STATUS)[keyof typeof LEAVE_STATUS]

/** PRD §8.2, verbatim. A submission opens UNDER_REVIEW and ends in one of the others. */
export const INNOVATION_STATUS = {
  UNDER_REVIEW: 'UNDER_REVIEW',
  ADOPTED: 'ADOPTED',
  NOT_PURSUED: 'NOT_PURSUED',
} as const
export type InnovationStatus = (typeof INNOVATION_STATUS)[keyof typeof INNOVATION_STATUS]

export const COMPLEXITY_MIN = 1
export const COMPLEXITY_MAX = 10

/**
 * Efficiency score weighting, approved by the owner 2026-08-08. See spec §3.9 for why
 * volume is excluded and why the minimum sample exists. Change these before Milestone 2
 * ships; changing them after managers have seen scores moves everyone's number.
 */
export const SCORE_WEIGHT_ON_TIME = 0.6
export const SCORE_WEIGHT_FIRST_TIME_PASS = 0.4
export const SCORE_MINIMUM_CLOSED_TASKS = 3
export const ON_TIME_WEIGHT = SCORE_WEIGHT_ON_TIME
export const FIRST_TIME_PASS_WEIGHT = SCORE_WEIGHT_FIRST_TIME_PASS
